import React from 'react';
import {Avatar, Button, Card, CardContent, Tooltip} from '@mui/material';
import {Bolt, SentimentDissatisfied, SentimentSatisfied, SentimentSatisfiedAlt, Star} from '@mui/icons-material';
import {useTranslation} from 'react-i18next';
import {OfferDTO} from '../user/on-sale/types';
import {formatMoney, moneyFromMinor} from '../utils/UnitConversion.utils';
import {useSelector} from 'react-redux';
import {RootState} from '../../store';
import {useNavigate} from 'react-router-dom';
import {useCurrency} from '../../hooks/useCurrency';
import {RARITY_COLORS, RarityGrade} from '../utils/RarityColors';
import {formatSteamImageUrl, useSteamImageUrl} from '../../hooks/useSteamImageUrl';

type Props = {
  offer: OfferDTO;
};

const OfferCard: React.FC<Props> = ({ offer }) => {
  const { t } = useTranslation();
  const currencySymbol = useSelector((state: RootState) => state.currency.symbol);
  const { convertFromUSD } = useCurrency();
  const navigate = useNavigate();

  const item = offer.item;
  const details = item.details;
  const catalog = item.catalog;

  // Format main item image URL with target CDN size
  const rawImg = catalog.imageLink ?? undefined;
  const img = useSteamImageUrl(rawImg, '360fx360f');

  const rarityColor = RARITY_COLORS[details?.rarity as RarityGrade] ?? '#ffffff';

  const floatValue = details?.floatValue ?? null;
  const floatPercent = floatValue === null ? 0 : Math.min(Math.max(floatValue * 100, 0), 100);

  const formatDeliveryTime = (minutes: number) => {
    if (minutes < 60) return `${minutes}m`;
    const h = Math.floor(minutes / 60);
    const m = minutes % 60;
    return `${h}h ${m}m`;
  };

  const handleViewOffer = () => {
    navigate(`/market/offer/${offer.id}`);
  };

  const renderBolts = () => {
    if (offer.sellerSuccessfulTradesCount === 0) return null;

    const time = offer.sellerMedianDeliveryTime;
    let activeBolts = 0;
    if (time < 10) activeBolts = 3;
    else if (time < 30) activeBolts = 2;
    else if (time < 120) activeBolts = 1;

    return (
        <Tooltip title={t('landing_page.offer_card.median_delivery', { time: formatDeliveryTime(offer.sellerMedianDeliveryTime) })} arrow>
          <div className="flex">
            {[1, 2, 3].map((i) => (
                <Bolt
                    key={i}
                    className={i <= activeBolts ? 'text-blue-500' : 'text-neutral-300 dark:text-neutral-700'}
                    sx={{
                      fontSize: 16,
                      marginLeft: -0.5
                    }}
                />
            ))}
          </div>
        </Tooltip>
    );
  };

  const renderSellerTrust = () => {
    if (offer.sellerTradeCount === 0) return null;

    const rate = (offer.sellerSuccessfulTradesCount / offer.sellerTradeCount) * 100;
    const showStar = offer.sellerTradeCount >= 100;

    let SmileyIcon = SentimentDissatisfied;
    let iconColor = '#ef4444';

    if (rate > 92) {
      SmileyIcon = SentimentSatisfiedAlt;
      iconColor = '#22c55e';
    } else if (rate > 80) {
      SmileyIcon = SentimentSatisfied;
      iconColor = '#eab308';
    }

    return (
        <div className="flex items-center gap-0.5 ml-1">
          <Tooltip title={t('landing_page.offer_card.success_rate', { rate: rate.toFixed(1), successful: offer.sellerSuccessfulTradesCount, total: offer.sellerTradeCount })} arrow>
            <SmileyIcon sx={{ fontSize: 16, color: iconColor }} />
          </Tooltip>
          {showStar && (
              <Tooltip title={t('landing_page.offer_card.experienced_seller', { count: offer.sellerSuccessfulTradesCount })} arrow>
                <Star sx={{ fontSize: 16, color: '#eab308' }} />
              </Tooltip>
          )}
        </div>
    );
  };

  const renderAttachments = () => {
    const attachments = item.attachments || [];
    if (attachments.length === 0) return null;

    return (
        <div className="absolute bottom-1 left-1 flex flex-col-reverse gap-0 z-20 backdrop-blur-[1px] rounded-md">
          {attachments.map((att, idx) => (
              <div key={att.id || idx} className="-mb-1 first:mb-0">
                <Tooltip title={att.marketHashName} arrow placement="right">
                  <div className="relative group/sticker">
                    <img
                        src={formatSteamImageUrl(att.imageLink, '100fx100f')}
                        alt={att.marketHashName || 'attachment'}
                        className="w-7 h-7 object-contain transition-transform duration-200 group-hover/sticker:scale-[1.8] group-hover/sticker:translate-x-1 drop-shadow-md"
                    />
                  </div>
                </Tooltip>
              </div>
          ))}
        </div>
    );
  };

  return (
      <Card
          className="w-72 relative bg-white dark:bg-neutral-900/50 transition-all duration-300 cursor-pointer shadow-sm hover:shadow-md"
          onClick={handleViewOffer}
      >
        <CardContent className="flex flex-col !p-3">
          {/* Header */}
          <div className="h-[36px]">
            <div className="text-light-text dark:text-dark-text font-semibold text-[0.80rem] leading-[1.25] tracking-[0.18px] line-clamp-2">
              {item.catalog.groupName ?? item.catalog.marketHashName ?? 'Unknown Item'}
            </div>

            <div className="flex items-center gap-1.5 mt-0.5">
              {item.catalog.isSouvenir && (
                  <span className="text-yellow-600 dark:text-yellow-400 font-semibold text-[0.7rem] tracking-[0.3px]">
                {t('landing_page.offer_card.souvenir')}
              </span>
              )}
              {item.catalog.isStattrak && (
                  <span className="text-orange-600 dark:text-orange-400 font-semibold text-[0.7rem] tracking-[0.3px]">
                {t('landing_page.offer_card.stattrak')}
              </span>
              )}
              {catalog?.wearName && (
                  <span className="text-gray-500 dark:text-gray-300 text-[0.7rem] font-medium tracking-[0.2px] capitalize">
                {catalog.wearName.replace('_', ' ').toLowerCase()}
                    {details?.phase && <> ({details?.phase})</>}
              </span>
              )}
            </div>
          </div>

          {/* Image Section */}
          <div
              className="relative flex justify-center items-end mb-3 rounded-md overflow-hidden group/img mt-2"
              style={{
                height: 160,
                background: `linear-gradient(to top, var(--rarity-gradient-color, ${rarityColor}99) 0%, transparent 100%)`,
              }}
          >
            <div className={`hidden [--rarity-gradient-color:${rarityColor}99] dark:[--rarity-gradient-color:${rarityColor}22]`} />

            <style dangerouslySetInnerHTML={{ __html: `
            .rarity-bg-${offer.id} {
              background: linear-gradient(to top, ${rarityColor}99 0%, transparent 100%);
            }
            .dark .rarity-bg-${offer.id} {
              background: linear-gradient(to top, ${rarityColor}22 0%, transparent 100%);
            }
          `}} />

            <div className={`absolute inset-0 rarity-bg-${offer.id}`} />

            {img ? (
                <img
                    src={img}
                    alt={item.catalog.marketHashName ?? 'item'}
                    className="w-44 h-44 object-contain transition-transform duration-500 group-hover/img:scale-110 z-10"
                    style={{ objectPosition: 'center 90%' }}
                />
            ) : (
                <Avatar sx={{ width: 80, height: 80, zIndex: 10 }} />
            )}

            {renderAttachments()}
          </div>

          {/* Price and Trust Info */}
          <div className="flex items-center justify-between mb-2">
            <div className="text-light-text dark:text-dark-text font-semibold flex gap-1 text-lg">
              <div className="font-medium dark:text-neutral-400 text-light-text">{currencySymbol}</div>
              <div>{formatMoney(convertFromUSD(moneyFromMinor(offer.priceMinor)))}</div>
            </div>

            <div className="flex items-center">
              {renderBolts()}
              {renderSellerTrust()}
            </div>
          </div>

          {/* Float Bar */}
          <div className="h-[40px] w-full">
            {floatValue !== null && (
                <>
                  <div
                      className="relative h-[6px] rounded-full w-full bg-neutral-200 dark:bg-neutral-800 overflow-visible"
                      style={{
                        background: 'linear-gradient(to right, #14532d 0%, #14532d 7%, #22c55e 7%, #22c55e 15%, #eab308 15%, #eab308 38%, #f87171 38%, #f87171 45%, #b91c1c 45%, #b91c1c 100%)',
                      }}
                  >
                    <div
                        className="absolute top-1/2 -translate-y-1/2 w-[2px] h-[10px] bg-white dark:bg-white rounded-sm shadow-[0_0_5px_rgba(255,255,255,0.8)] z-10"
                        style={{ left: `${floatPercent}%` }}
                    />
                  </div>
                  <div className="flex justify-between text-[0.65rem]  text-light-text dark:text-neutral-500 mt-1.5 font-semibold font-mono">
                    <div>{floatValue.toFixed(10)}</div>
                    <div>{details?.paintSeed !== undefined && details?.paintSeed !== null ? `#${details.paintSeed}` : ''}</div>
                  </div>
                </>
            )}
          </div>

          {/* CTA Button */}
          <Button
              variant="contained"
              fullWidth
              onClick={(e) => {
                e.stopPropagation();
                handleViewOffer();
              }}
              className="bg-dark-primary text-white !font-semibold py-2 rounded-xl transition-colors normal-case"
              sx={{ boxShadow: 'none', '&:hover': { boxShadow: 'none' } }}
          >
            {t('landing_page.offer_card.view_offer')}
          </Button>
        </CardContent>
      </Card>
  );
};

export default OfferCard;